/**
 * Lightweight date formatting helpers — no external dependency.
 * Inputs accept ISO strings or Date objects.
 */

const MS_MIN = 60 * 1000;
const MS_HOUR = 60 * MS_MIN;
const MS_DAY = 24 * MS_HOUR;

function toDate(input: string | Date): Date {
  return input instanceof Date ? input : new Date(input);
}

/**
 * Returns a short relative-time string ("just now", "5 min ago", "2 hours ago",
 * "Yesterday", "Apr 12"). Falls back to absolute date for older entries.
 */
export function formatRelative(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = toDate(input);
  const now = Date.now();
  const diff = now - date.getTime();

  if (diff < MS_MIN) return 'just now';
  if (diff < MS_HOUR) {
    const m = Math.floor(diff / MS_MIN);
    return `${m} min ago`;
  }
  if (diff < MS_DAY) {
    const h = Math.floor(diff / MS_HOUR);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 2 * MS_DAY) return 'Yesterday';
  if (diff < 7 * MS_DAY) {
    const d = Math.floor(diff / MS_DAY);
    return `${d} days ago`;
  }
  return formatAbsolute(date);
}

/**
 * Returns "Apr 12, 2026" for cross-year clarity.
 */
export function formatAbsolute(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = toDate(input);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Returns "Apr 12, 2026 14:30" — used for scheduled publish/expiry timestamps.
 */
export function formatDateTime(input: string | Date | null | undefined): string {
  if (!input) return '';
  const date = toDate(input);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
